/**
 * Badge Icon Uploader Component
 * Drag-and-drop file upload component for badge icons
 * SVG only, with size validation and preview
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface BadgeIconUploaderProps {
  onFileSelect: (file: File | null) => void;
  currentIconUrl?: string | null;
  disabled?: boolean;
  className?: string;
}

const MAX_FILE_SIZE = 256 * 1024; // 256KB

/**
 * SVG only, because that is the only thing the API has ever accepted:
 * `POST /v1/admin/badges/{badgeId}/icon` declares
 * `allowedMimeTypes: ['image/svg+xml']` and, since #1958 batch 1, also proves
 * the bytes parse as an SVG document. This component used to offer PNG in its
 * allowed types, its file-picker `accept`, its error message and its helper
 * text — inviting a file the server had never once accepted.
 *
 * The filename extension is the gate; the MIME type is corroborating evidence
 * that is allowed to be missing.
 *
 * - Extension alone is required, because type alone lets `.svgz` through:
 *   Chromium maps `svg,svgz` to the same `image/svg+xml`, so a gzipped SVG
 *   would pass a type check and then be refused by the server, which reads the
 *   bytes.
 * - An EMPTY `File.type` is accepted, and normalised below. The File API
 *   permits the empty string "if the type cannot be determined", and this is
 *   not a check we can afford to fail closed on: the multipart part's
 *   Content-Type is `File.type` (`authenticated-api.ts` appends the File
 *   directly) and the server rejects on that value BEFORE it reads any bytes,
 *   so a browser reporting "" would leave the user unable to upload at all,
 *   with no workaround.
 * - An explicit, contradictory type — `image/png` — is still refused.
 *
 * In practice current browsers do report `image/svg+xml` here: both Chromium
 * and Firefox hold it in a hardcoded table the OS cannot override (Chromium's
 * `kPrimaryMappings`, Firefox's `defaultMimeEntries`, commented "These are not
 * overridable"). That was not always so — Chromium kept svg in its
 * registry-overridable secondary table until version 100, which is where the
 * folklore about unreliable SVG MIME types comes from. The empty case is
 * handled because the specification permits it, not because it was observed.
 *
 * None of this is a security control. The server validates that the bytes
 * actually parse as an SVG document (#1958 batch 1); normalising a missing type
 * cannot smuggle anything past that.
 */
/** Shown in place of a preview whose image will not load. */
const PLACEHOLDER_ICON = `data:image/svg+xml;base64,${btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="#6b7280"><rect width="64" height="64" rx="8" fill="#f3f4f6"/><path d="M32 16a16 16 0 100 32 16 16 0 000-32zm0 4a12 12 0 110 24 12 12 0 010-24z"/></svg>'
)}`;

const ALLOWED_TYPES = ['image/svg+xml'];
const ALLOWED_EXTENSIONS = ['.svg'];

export function BadgeIconUploader({
  onFileSelect,
  currentIconUrl,
  disabled = false,
  className = '',
}: BadgeIconUploaderProps) {
  // `required` used to be declared on the props interface and was never
  // destructured here or passed by any caller — it read as though this
  // component could enforce a requirement, and it could not. BadgeFormModal
  // renders its own asterisk and refuses to submit without a file.
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file
  const validateFile = (file: File): string | null => {
    const name = file.name.toLowerCase();
    const hasAllowedExtension = ALLOWED_EXTENSIONS.some(ext =>
      name.endsWith(ext)
    );
    const typeIsAcceptable =
      file.type === '' || ALLOWED_TYPES.includes(file.type);
    if (!hasAllowedExtension || !typeIsAcceptable) {
      return 'Please select an SVG file';
    }
    if (file.size > MAX_FILE_SIZE) {
      // `>` permits exactly 256KB, so say so. This used to read "less than
      // 256KB", which is not what it does.
      return 'File size must be 256KB or less';
    }
    return null;
  };

  /**
   * Give the file an explicit `image/svg+xml` type when the browser did not.
   * The upload appends this File straight to a FormData, so its `type` becomes
   * the multipart part's Content-Type, and that is what the server checks
   * first.
   */
  const withSvgType = (file: File): File =>
    file.type === ''
      ? new File([file], file.name, {
          type: 'image/svg+xml',
          lastModified: file.lastModified,
        })
      : file;

  // Handle file selection
  const handleFileSelect = useCallback(
    (file: File | null) => {
      setError(null);

      if (!file) {
        setSelectedFile(null);
        onFileSelect(null);
        return;
      }

      const validationError = validateFile(file);
      if (validationError) {
        // Clear the selection as well as showing the error. Returning early
        // used to leave a previously accepted file both previewed and held by
        // the parent, so choosing a good file and then a bad one displayed the
        // error while the good file remained armed for submission.
        setError(validationError);
        setSelectedFile(null);
        onFileSelect(null);
        return;
      }

      const normalised = withSvgType(file);
      setSelectedFile(normalised);
      onFileSelect(normalised);
    },
    [onFileSelect]
  );

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
  };

  // Handle drag events
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [disabled, handleFileSelect]
  );

  // Remove selected file
  const handleRemove = () => {
    setSelectedFile(null);
    setError(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get preview URL.
  //
  // Created in an effect, not during render. `createObjectURL` is a side
  // effect: a render React discards mints a URL whose cleanup never runs, and
  // this app mounts under <StrictMode> (`main.tsx`), which double-invokes
  // render in development. Measured across three selections plus an unmount:
  // creating it in a `useMemo` produced 6 URLs and revoked 3; creating it here
  // produces 3 and revokes 3. An effect only runs for a render that commits, so
  // every URL created here has a matching revoke.
  //
  // One honest caveat, since an earlier version of this comment claimed
  // otherwise: on REPLACEMENT the old URL is revoked before the new one is
  // committed, so there is a brief window where the rendered `src` names a
  // revoked URL. A reviewer reproduced the ordering. It is invisible in
  // practice — the browser has already decoded the image it is displaying — and
  // closing it properly means deferring the revoke past a commit, which buys
  // very little for the complexity. Recorded rather than hidden.
  //
  // A badge with no icon has `currentIconUrl === ''`, which is falsy, so the
  // component shows the dropzone rather than an empty preview. That is the
  // behaviour we want and it is pinned by a test.
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedFile) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const previewUrl = objectUrl ?? currentIconUrl;

  // A new source deserves its own chance to load. Declared after `previewUrl`,
  // not before it — a `const` is not hoisted, and reading it above its
  // declaration throws at render.
  useEffect(() => setPreviewFailed(false), [previewUrl]);

  return (
    <div className={className}>
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors',
          isDragOver
            ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
            : 'border-gray-300 dark:border-gray-600',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-red-300 dark:border-red-600'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          // Preview mode
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              {/* Guarded with state rather than by reassigning `src` in the
                  handler. The first attempt swapped the source and set
                  `e.currentTarget.onerror = null` to stop it re-entering; that
                  does nothing, because React attaches this through its own
                  synthetic event system and clearing the DOM property leaves
                  the handler installed — demonstrated by dispatching two error
                  events and watching it run twice. Deriving the source from
                  state cannot re-enter at all. */}
              <img
                src={previewFailed ? PLACEHOLDER_ICON : previewUrl}
                alt="Badge icon preview"
                className="w-16 h-16 rounded-lg object-cover"
                onError={() => setPreviewFailed(true)}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {selectedFile ? selectedFile.name : 'Current icon'}
              </p>
              {selectedFile && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
            <div className="flex-shrink-0 flex items-center gap-3">
              {/* Preview mode hides the dropzone's "Choose file" and the input
                  itself is `hidden`, so without this an admin looking at an
                  already-uploaded icon has no way to pick a replacement — only
                  drag-and-drop, which is undiscoverable and unavailable to
                  anyone using the keyboard. The form beneath this component
                  says "Upload a new icon to replace the current one", so the
                  affordance has to exist. */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedFile ? 'Choose a different file' : 'Replace'}
              </button>

              {/* Remove is offered only for a local selection, because that is
                  the only thing it can do. Against an already-uploaded icon it
                  cleared the selection, `previewUrl` fell straight back to
                  `currentIconUrl`, and the same image stayed on screen — a
                  button reading "remove this icon" that removed nothing. There
                  is no endpoint that clears an icon, only one that sets it. */}
              {selectedFile && (
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ) : (
          // Upload mode
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Choose file
              </button>
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                or drag and drop
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              SVG up to 256KB
            </p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled}
          className="hidden"
          aria-label="Choose badge icon file"
        />
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
